import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useFinanceDataContext } from '../../contexts/FinanceDataContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { formatSmartCurrency } from '../../utils/formatCurrency';
import { ref, onValue, update } from 'firebase/database';
import { auth, database } from '../../firebase';
import {
  ACCOUNT_TYPES, normalizeAccountType, getDefaultEmojiForType,
  getAccountTypeLabel,
} from '../../utils/accountTypeMapping';
import moment from 'moment';
import { useTheme } from '../../contexts/ThemeContext';

const ACCOUNT_TYPE_ORDER = [
  ACCOUNT_TYPES.CHECKING, ACCOUNT_TYPES.CASH, ACCOUNT_TYPES.SAVINGS,
  ACCOUNT_TYPES.INVESTMENT, ACCOUNT_TYPES.RETIREMENT, ACCOUNT_TYPES.CREDIT,
];

const AccountsPage = () => {
  const {
    getBalanceItems, addBankAccount, updateBankAccountAmount, deleteItem,
  } = useFinanceDataContext();
  const { currencyCode } = useCurrency();
  const { theme } = useTheme();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncedAccounts, setSyncedAccounts] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [newType, setNewType] = useState(ACCOUNT_TYPES.CHECKING);
  const [newEmoji, setNewEmoji] = useState('');

  // Load synced accounts from Firebase
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      if (!user) return;
      const syncedRef = ref(database, `users/${user.uid}/syncedAccounts`);
      const unsubVal = onValue(syncedRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const parsed = Object.entries(data).map(([id, val]) => ({ id, ...val, source: 'plaid' }));
          setSyncedAccounts(parsed);
        } else {
          setSyncedAccounts([]);
        }
      });
      return () => unsubVal();
    });
    return () => unsub();
  }, []);

  const balances = useMemo(() => getBalanceItems() || [], [getBalanceItems, refreshKey]);

  // Merge manual + synced into unified list
  const allAccounts = useMemo(() => {
    const manual = balances.map((b) => ({
      ...b,
      source: 'manual',
      displayName: b.title,
      displayAmount: b.amount,
      includeInProjections: b.includeInProjections !== false, // default true for manual
      normalizedType: normalizeAccountType(b.accountType, b.title, b.emoji),
      lastUpdated: b.lastUpdated || b.createdAt,
    }));

    // Deduplicate synced accounts by keeping latest per mask+name combo
    const seenSynced = {};
    syncedAccounts.forEach((sa) => {
      const key = `${sa.name || sa.title}-${sa.mask || ''}`;
      if (!seenSynced[key] || (sa.updatedAt || '') > (seenSynced[key].updatedAt || '')) {
        seenSynced[key] = sa;
      }
    });

    const synced = Object.values(seenSynced).map((sa) => ({
      ...sa,
      displayName: sa.name || sa.title || 'Synced Account',
      displayAmount: (sa.balance || 0),
      includeInProjections: sa.includeInProjections !== false,
      normalizedType: sa.subtype || (sa.type === 'credit' ? 'credit' : 'checking'),
      lastUpdated: sa.updatedAt || sa.syncedAt,
      mask: sa.mask,
    }));

    return [...manual, ...synced];
  }, [balances, syncedAccounts]);

  // Split by inclusion
  const included = useMemo(() => allAccounts.filter((a) => a.includeInProjections), [allAccounts]);
  const notIncluded = useMemo(() => allAccounts.filter((a) => !a.includeInProjections), [allAccounts]);

  const cashBalance = useMemo(() => included.reduce((s, a) => s + (a.displayAmount || 0), 0), [included]);
  const totalAll = useMemo(() => allAccounts.reduce((s, a) => s + (a.displayAmount || 0), 0), [allAccounts]);

  const fmt = useCallback((amount, mode = 'long') => formatSmartCurrency(amount, { mode, currencyCode }), [currencyCode]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const handleAddAccount = async () => {
    if (!newTitle.trim()) return;
    const amount = parseFloat(newAmount) || 0;
    const emoji = newEmoji || getDefaultEmojiForType(newType);
    await addBankAccount({ title: newTitle.trim(), amount, accountType: newType, emoji });
    setNewTitle(''); setNewAmount(''); setNewType(ACCOUNT_TYPES.CHECKING); setNewEmoji('');
    setShowAddForm(false); refresh();
  };

  const handleSaveEdit = async (account) => {
    const v = parseFloat(editValue);
    if (isNaN(v)) return;
    await updateBankAccountAmount(account.id, v);
    setEditingId(null); setEditValue(''); refresh();
  };

  const handleDelete = async (account) => {
    if (!window.confirm(`Delete "${account.displayName}"?`)) return;
    await deleteItem(account.id); refresh();
  };

  const toggleInclusion = async (account) => {
    if (!auth.currentUser) return;
    const userId = auth.currentUser.uid;
    const path = account.source === 'plaid'
      ? `users/${userId}/syncedAccounts/${account.id}`
      : `users/${userId}/flo/balances/${account.id}`;
    const newVal = !account.includeInProjections;
    await update(ref(database, path), { includeInProjections: newVal });
    refresh();
  };

  const timeAgo = (ts) => {
    if (!ts) return '';
    const diff = moment().diff(moment(ts), 'minutes');
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  return (
    <div style={{ backgroundColor: theme.backgroundColor, minHeight: '100%', padding: '0 0 100px' }}>
      {/* Hero */}
      <div style={{ textAlign: 'center', padding: '28px 20px 20px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.tertiaryText, textTransform: 'uppercase', letterSpacing: 1.2 }}>Cash Balance</div>
        <div style={{ fontSize: 36, fontWeight: 800, color: theme.textColor, letterSpacing: -0.5, marginTop: 4 }}>{fmt(cashBalance)}</div>
        <div style={{ fontSize: 13, color: theme.tertiaryText, marginTop: 4 }}>
          NET (INCL. DEBT): {fmt(totalAll)}
        </div>
      </div>

      {/* Included in Projections */}
      <div style={{ padding: '0 20px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.tertiaryText, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, marginTop: 8 }}>
          Included in Projections
        </div>

        {included.map((account) => (
          <AccountRow theme={theme}
            key={account.id}
            account={account}
            fmt={fmt}
            timeAgo={timeAgo}
            editingId={editingId}
            editValue={editValue}
            onEditClick={(a) => { setEditingId(a.id); setEditValue(a.displayAmount.toFixed(2)); }}
            onEditChange={setEditValue}
            onSave={handleSaveEdit}
            onCancel={() => { setEditingId(null); setEditValue(''); }}
            onDelete={handleDelete}
            onToggle={toggleInclusion}
            included
          />
        ))}

        {included.length === 0 && (
          <div style={{ padding: '24px 0', fontSize: 14, color: theme.disabledText, textAlign: 'center' }}>
            No accounts included in projections
          </div>
        )}
      </div>

      {/* Not Included */}
      {notIncluded.length > 0 && (
        <div style={{ padding: '0 20px', marginTop: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.tertiaryText, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
            Not Included
          </div>

          {notIncluded.map((account) => (
            <AccountRow theme={theme}
              key={account.id}
              account={account}
              fmt={fmt}
              timeAgo={timeAgo}
              editingId={editingId}
              editValue={editValue}
              onEditClick={(a) => { setEditingId(a.id); setEditValue(a.displayAmount.toFixed(2)); }}
              onEditChange={setEditValue}
              onSave={handleSaveEdit}
              onCancel={() => { setEditingId(null); setEditValue(''); }}
              onDelete={handleDelete}
              onToggle={toggleInclusion}
            />
          ))}
        </div>
      )}

      {/* Add Account */}
      <div style={{ padding: '16px 20px' }}>
        {showAddForm ? (
          <div style={{ borderRadius: 16, padding: 20, border: `1px solid ${theme.borderColor}`, backgroundColor: theme.surfaceBackground }}>
            <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 16, color: theme.textColor }}>Add Account</div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Type</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {ACCOUNT_TYPE_ORDER.map((type) => (
                  <button key={type} onClick={() => { setNewType(type); setNewEmoji(getDefaultEmojiForType(type)); }}
                    style={{ padding: '5px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer', border: newType === type ? '2px solid #4882F7' : '1px solid #E5E7EB', backgroundColor: newType === type ? '#EFF6FF' : '#fff', color: newType === type ? '#4882F7' : '#999', fontWeight: newType === type ? 600 : 400 }}>
                    {getDefaultEmojiForType(type)} {getAccountTypeLabel(type)}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Name</label>
              <input type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g., Chase Checking" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Balance</label>
              <input type="number" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} placeholder="0.00" step="0.01" style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleAddAccount} style={{ flex: 1, padding: 12, backgroundColor: '#222', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>Add Account</button>
              <button onClick={() => { setShowAddForm(false); setNewTitle(''); setNewAmount(''); }} style={{ padding: '12px 20px', backgroundColor: theme.backgroundColor, color: theme.tertiaryText, border: `1px solid ${theme.borderColor}`, borderRadius: 12, fontSize: 16, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowAddForm(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: 14, border: `2px dashed ${theme.borderColor}`, borderRadius: 16, backgroundColor: 'transparent', cursor: 'pointer', color: theme.tertiaryText, fontSize: 15, fontWeight: 500 }}>+ Add Account</button>
        )}
      </div>
    </div>
  );
};

const AccountRow = ({ account, fmt, timeAgo, editingId, editValue, onEditClick, onEditChange, onSave, onCancel, onDelete, onToggle, included, theme }) => {
  const isEditing = editingId === account.id;
  const emoji = account.emoji || getDefaultEmojiForType(account.normalizedType);
  const isPlaid = account.source === 'plaid';
  const isCredit = account.normalizedType === 'credit';
  const amount = account.displayAmount || 0;
  const hasAPY = account.apy || account.APY || account.growthRate;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', padding: '14px 0',
      borderBottom: `1px solid ${theme.lightBorder}`, opacity: included ? 1 : 0.55,
    }}>
      {/* Toggle circle */}
      <button
        onClick={() => onToggle(account)}
        style={{
          width: 22, height: 22, borderRadius: 11, marginRight: 12, flexShrink: 0,
          border: included ? '2px solid #4882F7' : '2px solid #D1D5DB',
          backgroundColor: included ? '#4882F7' : 'transparent',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 0,
        }}
        title={included ? 'Remove from projections' : 'Include in projections'}
      >
        {included && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>✓</span>}
      </button>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: theme.textColor }}>{account.displayName}</span>
          {account.mask && (
            <span style={{ fontSize: 12, color: theme.disabledText }}>~{account.mask}</span>
          )}
          {isPlaid && (
            <span style={{ fontSize: 10, fontWeight: 600, color: '#4882F7', backgroundColor: '#EFF6FF', padding: '1px 6px', borderRadius: 4 }}>Linked</span>
          )}
          {isCredit && (
            <span style={{ fontSize: 10, fontWeight: 600, color: '#FF9500', backgroundColor: '#FFF7ED', padding: '1px 6px', borderRadius: 4 }}>CREDIT CARD</span>
          )}
          {hasAPY > 0 && (
            <span style={{ fontSize: 10, fontWeight: 600, color: '#3FD082', backgroundColor: '#ECFDF5', padding: '1px 6px', borderRadius: 4 }}>{hasAPY}% APY</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: theme.disabledText, marginTop: 2 }}>
          {account.lastUpdated ? `Updated ${timeAgo(account.lastUpdated)}` : ''}
          {!included && <span> · Excluded from projections</span>}
        </div>
      </div>

      {/* Amount */}
      {isEditing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="number" value={editValue} onChange={(e) => onEditChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSave(account); if (e.key === 'Escape') onCancel(); }}
            autoFocus step="0.01"
            style={{ width: 100, padding: '4px 8px', border: '2px solid #4882F7', borderRadius: 6, fontSize: 16, fontWeight: 700, textAlign: 'right', outline: 'none' }} />
          <button onClick={() => onSave(account)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3FD082', fontWeight: 700, fontSize: 16 }}>✓</button>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.tertiaryText }}>✕</button>
        </div>
      ) : (
        <span
          style={{ fontSize: 16, fontWeight: 600, color: amount >= 0 ? '#222' : '#EF4444', cursor: account.source === 'manual' ? 'pointer' : 'default', flexShrink: 0 }}
          onClick={() => { if (account.source === 'manual') onEditClick(account); }}
          title={account.source === 'manual' ? 'Click to edit' : ''}
        >
          {fmt(amount)}
        </span>
      )}

      {/* Delete (manual only) */}
      {account.source === 'manual' && (
        <button onClick={() => onDelete(account)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E5E7EB', fontSize: 13, marginLeft: 8, padding: 4 }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#EF4444')} onMouseLeave={(e) => (e.currentTarget.style.color = '#E5E7EB')}>✕</button>
      )}
    </div>
  );
};

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#999', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 };
const inputStyle = { width: '100%', padding: '12px 14px', border: '1px solid #999', borderRadius: 10, fontSize: 16, outline: 'none', boxSizing: 'border-box', color: 'inherit', backgroundColor: 'transparent' };

export default AccountsPage;
